<template>
  <v-app>
    <v-layout>
      <v-flex xs12>
        <!-- Loader for categories -->
        <v-container v-if="isLoadingCategories">
          <Loader></Loader>
        </v-container>

        <div v-else>
          <!-- CREATE STEPPER -->
          <div>
            <v-stepper non-linear v-model="step">
              <v-stepper-header>
                <template v-for="(s, si) in steps">
                  <v-stepper-step
                    :key="`step-${si}`"
                    :step="si + 1"
                    editable
                    :complete="step == si + 1"
                    >{{ s[locale] }}</v-stepper-step
                  >
                  <v-divider
                    :key="`s-${si}`"
                    v-if="si != steps.length - 1"
                  ></v-divider>
                </template>
              </v-stepper-header>
            </v-stepper>
          </div>

          <!-- Title -->
          <div
            class="headline mb-1 mt-6 text-uppercase text-center font-weight-black"
          >
            {{ steps[step - 1][locale] }}
          </div>

          <!-- Loader for questions -->
          <v-container v-if="isLoadingQuestions">
            <Loader></Loader>
          </v-container>

          <!-- Questions -->
          <v-container v-else-if="step < steps.length">
            <!-- InputsAll is v-model for all questions and has all the collected answers -->
            <v-layout
              class="py-2"
              v-for="(q, i) in questionsLoaded"
              :key="`question_${i}`"
            >
              <v-card class="mx-auto" shaped width="100%">
                <v-card-title
                  class="headline mb-1 orange darken-3 white--text"
                  >{{ q.header[locale] }}</v-card-title
                >
                <div
                  class="pa-3 pb-0"
                  v-for="(hint, hIndex) in q.hint[locale]"
                  :key="`hIndex${hIndex}`"
                >
                  <v-card-text class="pa-0">{{ hint }}</v-card-text>
                </div>

                <!-- CREATE INPUTS -->

                <!-- Radio group -->
                <v-col class="my-n2" v-if="q.type == 'radiolabels'">
                  <v-radio-group v-model="inputsAll[`${q.id}`]" row>
                    <v-radio
                      v-for="(n, nIndex) in q.labels[locale].length"
                      :key="`nIndex${nIndex}`"
                      :label="q.labels[locale][n - 1]"
                      color="primary"
                      :value="n"
                    >
                      ></v-radio
                    >
                  </v-radio-group>
                </v-col>

                <!-- Checkbox -->
                <v-col class="my-n2" v-if="q.type == 'checkbox'">
                  <v-checkbox
                    :label="q.labels[locale]"
                    color="primary"
                    v-model="inputsAll[`${q.id}`]"
                  ></v-checkbox>
                </v-col>

                <!-- CONTROLS -->
                <v-card-actions v-for="(c, cIndex) in q.controls" :key="cIndex">
                  <v-btn
                    @click="control(c, q, i)"
                    depressed
                    small
                    text
                    v-if="c"
                  >
                    <v-icon>mdi-plus</v-icon>
                    {{ c.text[locale] }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-layout>
          </v-container>

          <!-- PREVIEW -->
          <v-container v-else-if="step == steps.length">
            <v-row>
              <v-col>
                <Result
                  :inputsAll="inputsAll"
                  :categories="categories"
                  :questionsWeights="questionsWeights"
                />
              </v-col>
            </v-row>
          </v-container>

          <!-- FABS BOTTOM -->

          <transition name="fade">
            <v-btn
              class="rightbutton"
              fixed
              v-if="step < steps.length"
              transition="scroll-y-reverse-transition"
              dark
              fab
              :loading="false"
              color="primary"
              @click="step = step + 1"
            >
              <v-icon>mdi-arrow-right-bold</v-icon>
            </v-btn>
          </transition>

          <transition name="fade">
            <v-btn
              transition="scroll-y-reverse-transition"
              class="leftbutton"
              fixed
              v-if="step > 1"
              dark
              fab
              :loading="false"
              color="primary"
              @click="step = step - 1"
            >
              <v-icon>mdi-arrow-left-bold</v-icon>
            </v-btn>
          </transition>
        </div>
      </v-flex>
    </v-layout>
  </v-app>
</template>

<script>
import Loader from "../components/loader.component";
import Result from "../components/result.component";
import digicapQuestions from "../questions/digicap.questions";

export default {
  name: "MainPage",
  components: {
    Loader,
    Result
  },
  data() {
    return {
      isLoadingCategories: true,
      isLoadingQuestions: true,
      inputsAll: {},
      step: 1,
      steps: [],
      categories: [],
      answeredQuestions: {},
      questionsLoaded: [],
      questionsWeights: {}
    };
  },
  methods: {
    fetchCategories: function() {
      const categories = digicapQuestions.categories;
      this.steps = categories.map(category => category.name);
      this.categories = categories.slice();

      this.isLoadingCategories = false;
      this.fetchQuestions(1);
    },
    fetchQuestions: function(id) {
      if (this.answeredQuestions[id]) {
        this.questionsLoaded = this.answeredQuestions[id];
        this.isLoadingQuestions = false;
      } else {
        const questions = digicapQuestions.questions[`C${id}`];
        this.questionsLoaded = questions;

        if (id < this.steps.length) {
          this.answeredQuestions[id] = questions;
        }

        const inputsAllDefaultValues = {};

        for (const question of questions) {
          if (this.inputsAll[question.id] === undefined) {
            inputsAllDefaultValues[question.id] = question.defaultValue;
          }

          this.questionsWeights[question.id] = question.weight;
        }

        this.inputsAll = { ...this.inputsAll, ...inputsAllDefaultValues };

        localStorage.setItem(
          "questionsWeights",
          JSON.stringify(this.questionsWeights)
        );

        this.isLoadingQuestions = false;
        this.$vuetify.goTo(0, 0);
      }
    }
  },
  watch: {
    step: {
      handler(value) {
        localStorage.setItem("answers", JSON.stringify(this.inputsAll));

        if (value < this.categories.length) {
          this.isLoadingQuestions = true;
          this.fetchQuestions(value);
        }
      }
    }
  },
  created() {
    const answers = localStorage.getItem("answers");

    if (answers) {
      this.inputsAll = JSON.parse(answers);
    }

    const questionsWeights = localStorage.getItem("questionsWeights");

    if (questionsWeights) {
      this.questionsWeights = JSON.parse(questionsWeights);
    }

    this.fetchCategories();
  },
  computed: {
    locale() {
      return this.$store.getters.getLocale;
    }
  }
};
</script>

<style scoped>
.rightbutton {
  bottom: 3vh;
  right: 4vw;
}
.leftbutton {
  bottom: 3vh;
  right: calc(65px + 4vw);
}
</style>
